<template lang="pug">
  div
    yz-control-group(:direction="direction" :spacing="spacing" :has-error="hasError")
      slot
    .yz-regular-body-xs.ml-3.error--text.mt-2.mb-3(v-if="computedErrorMessage") {{ computedErrorMessage }}
</template>

<script>
import YzControlGroup from './YzControlGroup.vue';

export default {
  name: 'YzCheckboxGroup',
  components: {
    YzControlGroup
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    direction: {
      type: String,
      default: 'vertical',
      validator: value => ['vertical', 'horizontal'].includes(value)
    },
    spacing: {
      type: [Number, String],
      default: 20
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredErrorMessage: {
      type: String,
      default: 'This field is required'
    },
    // Add rules prop for more flexible validation
    rules: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      hasBeenValidated: false,
      internalErrorMessages: []
    }
  },
  computed: {
    // Merge required validation into rules array
    mergedRules() {
      const rules = [...this.rules];
      
      // If required is true, prepend the required validation rule
      if (this.required) {
        rules.unshift(v => (v && v.length > 0) || this.requiredErrorMessage);
      }
      
      return rules;
    },
    computedErrorMessage() {
      // Simplified - only check merged rules validation results
      if (this.internalErrorMessages.length > 0) {
        return this.hasBeenValidated ? this.internalErrorMessages[0] : '';
      }
      return '';
    },
    hasError() {
      return !!this.computedErrorMessage;
    }
  },
  watch: {
    value: {
      handler() {
        // Only validate if already been validated (for reactive validation)
        if (this.hasBeenValidated) {
          this.validate();
        }
      },
      immediate: false
    },
    mergedRules: {
      handler() {
        this.validate();
      },
      immediate: false,
      deep: true
    }
  },
  mounted() {
    // Register with parent v-form if it exists
    this.form = this.getForm();
    if (this.form && this.form.register) {
      this.form.register(this);
    }
  },
  beforeDestroy() {
    // Unregister from parent v-form
    if (this.form && this.form.unregister) {
      this.form.unregister(this);
    }
  },
  provide() {
    return {
      checkboxGroup: this
    }
  },
  methods: {
    updateValue(checkboxValue, isChecked) {
      const currentValue = [...this.value];
      if (isChecked) {
        if (!currentValue.includes(checkboxValue)) {
          currentValue.push(checkboxValue);
        }
      } else {
        const index = currentValue.indexOf(checkboxValue);
        if (index > -1) {
          currentValue.splice(index, 1);
        }
      }
      this.$emit('input', currentValue);
      this.validate(true);
    },
    // Main validation method that v-form will call
    validate(force = false) {
      if (force) {
        this.hasBeenValidated = true;
      }

      // If not yet validated and not forced, don't show errors
      if (!this.hasBeenValidated && !force) {
        return true;
      }

      this.hasBeenValidated = true;
      this.internalErrorMessages = [];

      // Run merged rules (includes required validation if set)
      if (this.mergedRules.length > 0) {
        for (const rule of this.mergedRules) {
          const result = typeof rule === 'function' ? rule(this.value) : rule;
          if (result !== true) {
            this.internalErrorMessages.push(result || 'Invalid value');
          }
        }

        if (this.internalErrorMessages.length > 0) {
          return false;
        }
      }

      return true;
    },

    // Just clear validation errors without resetting value
    resetValidation() {
      this.hasBeenValidated = false;
      this.internalErrorMessages = [];
    },

    // Helper method to find parent v-form
    getForm() {
      let parent = this.$parent;

      while (parent) {
        // Check if it's a v-form by name or by checking for form methods
        if (parent.$options.name === 'v-form' || (parent.register && parent.unregister && parent.validate)) {
          return parent;
        }

        // Also check if parent has a form ref (for custom form wrappers)
        if (parent.$refs && parent.$refs.form &&
            parent.$refs.form.register && parent.$refs.form.unregister) {
          return parent.$refs.form;
        }

        parent = parent.$parent;
      }

      return null;
    }
  }
}
</script>